import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { toast, Toaster } from 'sonner';
import axios from 'axios';
import { Music, Check, Trash2, Loader2, ArrowLeft, RefreshCw, Camera, Clock, User, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${API}/api`;

export default function DJLiveDashboard() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/dj-live/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      if (!data) setError(err.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [bookingId, token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const markPlayed = async (requestId) => {
    try {
      await axios.put(`${API_BASE}/event-experience/${bookingId}/requests/${requestId}/played`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(prev => ({
        ...prev,
        requests: prev.requests.map(r => r.id === requestId ? { ...r, status: 'played', played_at: new Date().toISOString() } : r)
      }));
    } catch (err) {
      toast.error('Failed to mark as played');
    }
  };

  const unmarkPlayed = async (requestId) => {
    try {
      await axios.put(`${API_BASE}/event-experience/${bookingId}/requests/${requestId}/unplay`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(prev => ({
        ...prev,
        requests: prev.requests.map(r => r.id === requestId ? { ...r, status: 'pending', played_at: null } : r)
      }));
    } catch (err) {
      toast.error('Failed to unmark');
    }
  };

  const removeRequest = async (requestId) => {
    try {
      await axios.delete(`${API_BASE}/event-experience/${bookingId}/requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(prev => ({
        ...prev,
        requests: prev.requests.filter(r => r.id !== requestId)
      }));
      toast.success('Request removed');
    } catch (err) {
      toast.error('Failed to remove');
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <Loader2 className="w-10 h-10 animate-spin text-cyan-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-black px-4">
      <div className="text-center">
        <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-white mb-2">Error</h1>
        <p className="text-white/50 mb-4">{error}</p>
        <Button onClick={() => navigate('/bookings')} variant="outline" className="border-white/20 text-white">
          <ArrowLeft className="w-4 h-4 mr-2" />Back to Bookings
        </Button>
      </div>
    </div>
  );

  const pending = (data?.requests || []).filter(r => r.status === 'pending');
  const played = (data?.requests || []).filter(r => r.status === 'played');
  const exp = data?.experience || {};

  return (
    <div className="min-h-screen bg-black text-white" data-testid="dj-live-dashboard">
      <Toaster position="top-right" theme="dark" />

      {/* Top Bar */}
      <header className="sticky top-0 z-40 bg-black/90 backdrop-blur-xl border-b border-white/10 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/bookings')} className="p-2 rounded-xl hover:bg-white/10 transition-colors" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white/60" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-white">{exp.event_title || 'Live Requests'}</h1>
              <p className="text-white/40 text-xs">{pending.length} pending &middot; {played.length} played &middot; {data?.photos_count || 0} photos</p>
            </div>
          </div>
          <button onClick={fetchData} className="p-2.5 rounded-xl bg-white/5 hover:bg-white/10 transition-colors" data-testid="refresh-btn">
            <RefreshCw className="w-5 h-5 text-cyan-400" />
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-4 space-y-6">
        {/* Pending Requests */}
        <div data-testid="pending-section">
          <h2 className="text-sm font-semibold text-cyan-400 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Clock className="w-4 h-4" />Up Next ({pending.length})
          </h2>
          {pending.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-2xl border border-white/5">
              <Music className="w-12 h-12 text-white/10 mx-auto mb-3" />
              <p className="text-white/30">No pending requests</p>
            </div>
          ) : (
            <div className="space-y-2">
              {pending.map(r => (
                <div key={r.id} className="bg-white/5 rounded-2xl border border-white/10 p-4 flex items-center gap-4" data-testid={`pending-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-semibold text-base truncate">{r.song_title}</p>
                      {!r.is_in_library && (
                        <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full shrink-0">Not in Library</span>
                      )}
                    </div>
                    <p className="text-white/50 text-sm">{r.artist}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-white/30 text-xs flex items-center gap-1"><User className="w-3 h-3" />{r.guest_name}</span>
                      {r.note && <span className="text-cyan-400/70 text-xs truncate">{r.note}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => markPlayed(r.id)}
                      className="p-3 rounded-xl bg-green-500/20 hover:bg-green-500/40 transition-colors"
                      title="Mark as played"
                      data-testid={`play-${r.id}`}
                    >
                      <Check className="w-6 h-6 text-green-400" />
                    </button>
                    <button
                      onClick={() => removeRequest(r.id)}
                      className="p-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 transition-colors"
                      title="Remove"
                      data-testid={`remove-${r.id}`}
                    >
                      <Trash2 className="w-5 h-5 text-red-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Played */}
        {played.length > 0 && (
          <div data-testid="played-section">
            <h2 className="text-sm font-semibold text-green-400 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Check className="w-4 h-4" />Played ({played.length})
            </h2>
            <div className="space-y-1">
              {played.map(r => (
                <div key={r.id} className="bg-white/[0.03] rounded-xl p-3 flex items-center gap-3 opacity-60" data-testid={`played-${r.id}`}>
                  <Check className="w-5 h-5 text-green-400/60 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-white/60 text-sm truncate">{r.song_title} &mdash; {r.artist}</p>
                    <p className="text-white/30 text-xs">{r.guest_name}</p>
                  </div>
                  <button
                    onClick={() => unmarkPlayed(r.id)}
                    className="text-xs text-white/20 hover:text-white/50 transition-colors px-2 py-1"
                    title="Move back to pending"
                    data-testid={`unplay-${r.id}`}
                  >
                    Undo
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
